import React, { useState, useEffect, useRef } from 'react';
import QRCode from 'qrcode';

function QRCodeDisplay({ value, size = 120 }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    if (canvasRef.current && value) {
      QRCode.toCanvas(canvasRef.current, value, {
        width: size, margin: 1,
        color: { dark: '#0a2342', light: '#ffffff' }
      });
    }
  }, [value, size]);
  return <canvas ref={canvasRef} style={{ borderRadius: '8px' }} />;
}

// ═══════════════════════════════
// SMART UPLOAD MODAL
// ═══════════════════════════════
function SmartUploadModal({ user, onClose, onSuccess }) {
  const [step, setStep] = useState('select');
  const [file, setFile] = useState(null);
  const [detected, setDetected] = useState(null);
  const [form, setForm] = useState({ reportType: 'Blood Test', hospitalName: '', prescriptionName: '', reportName: '' });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleFileSelect = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setStep('detecting');
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', f);
      const res = await fetch(`${process.env.REACT_APP_AI_URL}/detect-type`, { method: 'POST', body: formData });
      const data = await res.json();
      setDetected(data);
      setForm(prev => ({ ...prev, prescriptionName: f.name, reportName: f.name }));
      setStep('questions');
    } catch {
      setDetected({ type: 'report', has_medicines: false, has_lab_results: true });
      setStep('questions');
    }
    setLoading(false);
  };

  const handleSave = async () => {
    if (!file || !detected) return;
    setLoading(true);
    setMessage('');
    try {
      const formData = new FormData();
      formData.append('File', file);
      formData.append('PatientId', user.id);
      formData.append('ReportType', form.reportType);
      formData.append('HospitalName', form.hospitalName);
      const customName = detected?.type === 'prescription'
        ? (form.prescriptionName || file.name)
        : detected?.type === 'both'
        ? (form.reportName || form.prescriptionName || file.name)
        : (form.reportName || file.name);
      formData.append('Name', customName);
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/Reports/upload`, { method: 'POST', body: formData });
      const data = await res.json();
      if (res.ok) {
        setMessage('✅ ' + data.message);
        setStep('done');
        setTimeout(() => { onSuccess && onSuccess(); onClose(); }, 2000);
      } else {
        setMessage('❌ ' + data.message);
      }
    } catch { setMessage('❌ Cannot connect to server.'); }
    setLoading(false);
  };

  const typeConfig = {
    report: { icon: 'description', color: '#0B3C95', bg: '#EAF2FF', label: '📋 Detected as Lab Report!', desc: 'Will be saved to your Reports tab' },
    prescription: { icon: 'medication', color: '#8B5CF6', bg: '#F0EBFB', label: '💊 Detected as Prescription!', desc: 'Medicines will be saved to your Meds tab' },
    both: { icon: 'note_add', color: '#00A884', bg: '#E6F7F4', label: '📋 + 💊 Detected as Report & Prescription!', desc: 'Saved to both Reports and Meds tabs' },
  };
  const config = detected ? typeConfig[detected.type] || typeConfig.report : typeConfig.report;

  return (
    <div className="fixed inset-0 z-[200] bg-black/60 flex items-end" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="w-full bg-white rounded-t-3xl shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-gray-200 rounded-full"></div>
        </div>
        <div className="px-5 pb-8 pt-2">
          <div className="relative mb-5">
            <button onClick={onClose} className="absolute top-0 right-0 w-11 h-11 bg-gray-100 rounded-full flex items-center justify-center">
              <span className="material-symbols-outlined text-[#0D1B2A]" style={{ fontSize: '22px' }}>close</span>
            </button>
            <h2 className="font-black text-[#0D1B2A] text-3xl pr-14">Smart Upload</h2>
          </div>

          {step === 'select' && (
            <div className="space-y-4">
              <p className="text-gray-500 text-base leading-snug mb-1">AI will auto-detect if it's a report, prescription, or both!</p>
              <label className="w-full border border-gray-100 bg-white rounded-3xl p-4 flex items-center gap-4 cursor-pointer active:scale-[0.99] transition-all shadow-sm">
                <div className="w-16 h-16 rounded-full flex items-center justify-center shrink-0" style={{ background: '#fbe1ea' }}>
                  <span className="material-symbols-outlined text-2xl" style={{ color: '#e11d74' }}>photo_camera</span>
                </div>
                <div className="flex-1">
                  <p className="font-extrabold text-[#0D1B2A] text-lg">Take Photo</p>
                  <p className="text-sm text-gray-500 mt-0.5">Use camera to capture document</p>
                </div>
                <span className="material-symbols-outlined" style={{ fontSize: '26px', color: '#7c3aed' }}>chevron_right</span>
                <input type="file" accept="image/*" capture="environment" onChange={handleFileSelect} className="hidden" />
              </label>
              <label className="w-full border border-gray-100 bg-white rounded-3xl p-4 flex items-center gap-4 cursor-pointer active:scale-[0.99] transition-all shadow-sm">
                <div className="w-16 h-16 rounded-full flex items-center justify-center shrink-0" style={{ background: '#eceafc' }}>
                  <span className="material-symbols-outlined text-2xl" style={{ color: '#5a3fd6' }}>image</span>
                </div>
                <div className="flex-1">
                  <p className="font-extrabold text-[#0D1B2A] text-lg">Choose from Gallery</p>
                  <p className="text-sm text-gray-500 mt-0.5">Select photo from your device</p>
                </div>
                <span className="material-symbols-outlined" style={{ fontSize: '26px', color: '#7c3aed' }}>chevron_right</span>
                <input type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
              </label>
              <label className="w-full border border-gray-100 bg-white rounded-3xl p-4 flex items-center gap-4 cursor-pointer active:scale-[0.99] transition-all shadow-sm">
                <div className="w-16 h-16 rounded-full flex items-center justify-center shrink-0" style={{ background: '#ede7fb' }}>
                  <span className="material-symbols-outlined text-2xl" style={{ color: '#7c3aed' }}>upload_file</span>
                </div>
                <div className="flex-1">
                  <p className="font-extrabold text-[#0D1B2A] text-lg">Upload File</p>
                  <p className="text-sm text-gray-500 mt-0.5">PDF, JPG, PNG from files</p>
                </div>
                <span className="material-symbols-outlined" style={{ fontSize: '26px', color: '#7c3aed' }}>chevron_right</span>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileSelect} className="hidden" />
              </label>
            </div>
          )}

          {step === 'detecting' && (
            <div className="flex flex-col items-center py-8 space-y-4">
              <div className="w-16 h-16 bg-[#EAF2FF] rounded-2xl flex items-center justify-center">
                <span className="material-symbols-outlined text-[#0B3C95] text-3xl animate-pulse" style={{ fontVariationSettings: '"FILL" 1' }}>auto_awesome</span>
              </div>
              <p className="font-bold text-[#0D1B2A]">AI is reading your document...</p>
              <p className="text-xs text-gray-400 text-center">Detecting if it's a report, prescription, or both</p>
              <div className="flex gap-1">
                {[0, 0.15, 0.3].map((d, i) => (
                  <div key={i} className="w-2 h-2 bg-[#0B3C95] rounded-full animate-bounce" style={{ animationDelay: `${d}s` }}></div>
                ))}
              </div>
            </div>
          )}

          {step === 'questions' && detected && (
            <div className="space-y-4">
              <div className="rounded-2xl p-4 flex items-center gap-3" style={{ background: config.bg }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-white/60">
                  <span className="material-symbols-outlined" style={{ color: config.color, fontSize: '22px', fontVariationSettings: '"FILL" 1' }}>{config.icon}</span>
                </div>
                <div>
                  <p className="font-black text-[#0D1B2A] text-sm">{config.label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{config.desc}</p>
                </div>
              </div>

              {(detected.type === 'report' || detected.type === 'both') && (
                <div className="space-y-3">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Report Details</p>
                  <input type="text" placeholder="Report Name (e.g. Blood Test Jan 2026)"
                    value={form.reportName} onChange={e => setForm({ ...form, reportName: e.target.value })}
                    className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm bg-gray-50 outline-none focus:border-[#0B3C95]" />
                  <select value={form.reportType} onChange={e => setForm({ ...form, reportType: e.target.value })}
                    className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm bg-gray-50 outline-none focus:border-[#0B3C95]">
                    {['Blood Test', 'Urine Test', 'X-Ray', 'MRI', 'CT Scan', 'ECG', 'Other'].map(t => <option key={t}>{t}</option>)}
                  </select>
                  <input type="text" placeholder="Hospital / Lab Name (optional)"
                    value={form.hospitalName} onChange={e => setForm({ ...form, hospitalName: e.target.value })}
                    className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm bg-gray-50 outline-none focus:border-[#0B3C95]" />
                </div>
              )}

              {(detected.type === 'prescription' || detected.type === 'both') && (
                <div className="space-y-3">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Prescription Details</p>
                  <input type="text" placeholder="Prescription Name (e.g. Dr. Sharma's Prescription)"
                    value={form.prescriptionName} onChange={e => setForm({ ...form, prescriptionName: e.target.value })}
                    className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm bg-gray-50 outline-none focus:border-[#0B3C95]" />
                </div>
              )}

              {message && (
                <div className={`p-3 rounded-xl text-sm font-semibold ${message.includes('✅') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  {message}
                </div>
              )}

              <button onClick={handleSave} disabled={loading}
                className="w-full py-4 text-white font-bold rounded-2xl text-sm active:scale-95 disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #0B3C95, #1E65FF)', boxShadow: '0 8px 25px rgba(11,60,149,0.3)' }}>
                {loading ? '⏳ Saving...' : '💾 Save Document'}
              </button>
            </div>
          )}

          {step === 'done' && (
            <div className="flex flex-col items-center py-6 space-y-3">
              <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center">
                <span className="material-symbols-outlined text-green-500 text-4xl" style={{ fontVariationSettings: '"FILL" 1' }}>check_circle</span>
              </div>
              <p className="font-black text-[#0D1B2A]">Saved Successfully!</p>
              <p className="text-xs text-gray-400 text-center">{message}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════
// HOME TAB
// ═══════════════════════════════
function HomeTab({ user, setActiveTab }) {
  const [reports, setReports] = useState([]);
  const [accessRequests, setAccessRequests] = useState([]);
  const [approvedDoctors, setApprovedDoctors] = useState([]);
  const [holisticFeedbacks, setHolisticFeedbacks] = useState([]); // ✅ NEW
  const [message, setMessage] = useState('');
  const [qrOpen, setQrOpen] = useState(false);
  const [showSmartUpload, setShowSmartUpload] = useState(false);

  const fetchData = async () => {
    try {
      const reportsRes = await fetch(`${process.env.REACT_APP_API_URL}/api/Reports/${user.id}`);
      const reportsData = await reportsRes.json();
      setReports(reportsData.slice(0, 2));

      const accessRes = await fetch(`${process.env.REACT_APP_API_URL}/api/Access/requests/${user.id}`);
      const accessData = await accessRes.json();
      setAccessRequests(accessData.filter(r => r.status === 'pending').slice(0, 2));
      setApprovedDoctors(accessData.filter(r => r.status === 'approved').slice(0, 3));

      // ✅ Fetch holistic feedbacks from doctors
      const feedbackRes = await fetch(`${process.env.REACT_APP_API_URL}/api/Reports/holistic-feedback/${user.id}`);
      const feedbackData = await feedbackRes.json();
      setHolisticFeedbacks(feedbackData.slice(0, 3));
    } catch { }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line

  const handleApprove = async (requestId) => {
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/api/Access/approve`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ requestId }) });
      setMessage('✅ Access approved!');
      setAccessRequests(prev => prev.filter(r => r.id !== requestId));
    } catch { }
  };

  const handleDeny = async (requestId) => {
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/api/Access/reject`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ requestId }) });
      setAccessRequests(prev => prev.filter(r => r.id !== requestId));
    } catch { }
  };

  return (
    <div className="px-4 space-y-5 pb-6">

      {showSmartUpload && (
        <SmartUploadModal user={user} onClose={() => setShowSmartUpload(false)} onSuccess={() => fetchData()} />
      )}

      {qrOpen && (
        <div className="fixed inset-0 z-[200] bg-black/70 flex items-center justify-center p-6" onClick={() => setQrOpen(false)}>
          <div className="bg-white rounded-3xl p-6 flex flex-col items-center shadow-2xl w-full max-w-xs" onClick={e => e.stopPropagation()}>
            <p className="font-black text-[#0B3C95] text-lg mb-1">Your Patient QR Code</p>
            <p className="text-gray-400 text-xs mb-4 text-center">Doctors scan this to access your medical records</p>
            <div className="p-3 bg-gray-50 rounded-2xl border border-gray-100">
              <QRCodeDisplay value={user?.patientUid || user?.id?.toString() || 'MHJ'} size={200} />
            </div>
            <p className="font-mono text-[#0B3C95] font-bold text-sm mt-3 text-center break-all">{user?.patientUid}</p>
            <button onClick={() => setQrOpen(false)} className="mt-4 w-full py-3 bg-[#0B3C95] text-white font-bold rounded-2xl text-sm active:scale-95">Close</button>
          </div>
        </div>
      )}

      {/* Greeting */}
      <div>
        <p className="text-gray-500 font-semibold text-base flex items-center gap-1.5">
          {(() => {
            const h = new Date().getHours();
            if (h < 12) return '🌅 Good Morning';
            if (h < 17) return '☀️ Good Afternoon';
            if (h < 21) return '🌆 Good Evening';
            return '🌙 Good Night';
          })()}
        </p>
        <h1 className="text-3xl font-extrabold text-[#0D1B2A] tracking-tight mt-0.5">{user?.name?.split(' ')[0] || 'User'}</h1>
      </div>

      {/* Patient ID Card */}
      <div className="relative rounded-3xl overflow-hidden shadow-lg" style={{ background: 'linear-gradient(160deg, #ffffff 0%, #f3f0fb 100%)' }}>
        <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: 'linear-gradient(180deg, #e62e8a, #8b3fd6)' }} />
        <div className="flex items-start justify-between gap-4 p-5 pl-7">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-xs font-extrabold tracking-wider uppercase mb-3" style={{ color: '#1a1c2e' }}>
              <span className="material-symbols-outlined" style={{ fontSize: '20px', color: '#a12fb8' }}>shield</span>
              YOUR PATIENT ID
            </div>
            <p className="text-lg font-black font-mono whitespace-nowrap overflow-hidden text-ellipsis mb-2"
              style={{ background: 'linear-gradient(90deg, #e62e8a, #8b3fd6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              {user?.patientUid || 'MHJ-XXXX'}
            </p>
            <p className="text-gray-500 text-sm">Show this ID when you visit a provider</p>
            <div className="h-px bg-gray-200 my-3"></div>
            <div className="flex items-center gap-2 text-gray-600 text-sm">
              <span className="material-symbols-outlined" style={{ fontSize: '17px', color: '#8b3fd6' }}>smartphone</span>
              Tap the QR code to expand
            </div>
          </div>
          <button onClick={() => setQrOpen(true)} className="bg-white rounded-2xl p-3 shadow-md shrink-0 active:scale-95 transition-all flex flex-col items-center">
            <QRCodeDisplay value={user?.patientUid || user?.id?.toString() || 'MHJ'} size={100} />
            <p className="text-xs font-bold mt-2" style={{ background: 'linear-gradient(90deg, #e62e8a, #8b3fd6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Tap to expand
            </p>
          </button>
        </div>
      </div>

      {/* Smart Upload */}
      <button onClick={() => setShowSmartUpload(true)}
        className="w-full rounded-3xl p-4 flex items-center gap-4 active:scale-[0.99] transition-all text-left border-2 border-dashed"
        style={{ background: 'linear-gradient(160deg, #ffffff 0%, #f3f0fb 100%)', borderColor: '#c9b6f0' }}>
        <div className="w-14 h-14 rounded-full flex items-center justify-center shadow-sm shrink-0" style={{ background: '#ece3fa' }}>
          <span className="material-symbols-outlined text-2xl" style={{ color: '#7c3aed' }}>document_scanner</span>
        </div>
        <div className="flex-1">
          <p className="font-extrabold text-[#0D1B2A] text-base">Smart Upload</p>
          <p className="text-sm text-gray-500 mt-0.5">AI auto-detects report or prescription</p>
        </div>
        <span className="material-symbols-outlined" style={{ fontSize: '28px', color: '#7c3aed' }}>chevron_right</span>
      </button>

      {/* Quick Actions */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { tab: 'meds', icon: 'pill', label: 'My Meds', color: '#2A75FF', bg: '#EAF2FF' },
          { tab: 'reports', icon: 'upload_file', label: 'Upload', color: '#00A884', bg: '#E6F7F4' },
          { tab: 'ai', icon: 'auto_awesome', label: 'Ask AI', color: '#8B5CF6', bg: '#F0EBFB' },
        ].map(item => (
          <button key={item.tab} onClick={() => setActiveTab(item.tab)}
            className="bg-white rounded-2xl p-4 flex flex-col items-center justify-center gap-2.5 border border-gray-100 shadow-sm active:scale-95 transition-all aspect-square">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: item.bg }}>
              <span className="material-symbols-outlined text-2xl" style={{ color: item.color, fontVariationSettings: '"FILL" 1' }}>{item.icon}</span>
            </div>
            <p className="font-bold text-[#0D1B2A] text-xs text-center">{item.label}</p>
          </button>
        ))}
      </div>

      {/* Recent Reports */}
      <div>
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-lg font-extrabold text-[#0D1B2A]">Recent Reports</h2>
          <button onClick={() => setActiveTab('reports')} className="text-xs font-bold text-[#0B3C95] flex items-center gap-0.5">
            View All <span className="material-symbols-outlined text-sm">chevron_right</span>
          </button>
        </div>
        {reports.length === 0 ? (
          <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-[#EAF2FF] rounded-2xl flex items-center justify-center mb-4">
              <span className="material-symbols-outlined text-[#0B3C95] text-4xl">description</span>
            </div>
            <h3 className="text-base font-extrabold text-[#0D1B2A]">No Reports Yet</h3>
            <p className="text-xs text-gray-400 mt-1 mb-4">Upload your first report to get AI insights</p>
            <button onClick={() => setShowSmartUpload(true)}
              className="px-6 py-3 text-white font-bold text-xs rounded-2xl flex items-center gap-2 shadow-lg active:scale-95"
              style={{ background: '#0B3C95' }}>
              <span className="material-symbols-outlined text-base">cloud_upload</span>
              Smart Upload
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {reports.map(report => (
              <div key={report.id} className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm cursor-pointer active:scale-95" onClick={() => setActiveTab('reports')}>
                <div className="w-10 h-10 bg-[#EAF2FF] rounded-xl flex items-center justify-center mb-2">
                  <span className="material-symbols-outlined text-[#0B3C95]" style={{ fontSize: '20px' }}>description</span>
                </div>
                <p className="font-bold text-[#0D1B2A] text-sm truncate">{report.fileName}</p>
                <p className="text-xs text-gray-400 mt-0.5">{new Date(report.uploadDate).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ✅ Doctor's Overall Notes — Holistic Feedback */}
      {holisticFeedbacks.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-lg font-extrabold text-[#0D1B2A]">Doctor's Notes</h2>
            <span className="w-5 h-5 bg-blue-500 text-white text-xs font-bold rounded-full flex items-center justify-center">{holisticFeedbacks.length}</span>
          </div>
          <div className="space-y-2">
            {holisticFeedbacks.map(fb => (
              <div key={fb.id} className="bg-blue-50 rounded-2xl p-4 border border-blue-100 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-[#0B3C95] rounded-full flex items-center justify-center text-white font-black text-xs shrink-0">
                    {(fb.doctorName || 'D').charAt(0)}
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-black text-blue-700">Dr. {fb.doctorName}</p>
                    <p className="text-[10px] text-blue-400">{new Date(fb.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                  </div>
                  <span className="text-[10px] font-bold px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full">Overall Note</span>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">{fb.feedback}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Doctor Access */}
      {approvedDoctors.length > 0 && (
        <div>
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-lg font-extrabold text-[#0D1B2A]">Doctor Access</h2>
            <button onClick={() => setActiveTab('access')} className="text-xs font-bold text-[#0B3C95] flex items-center gap-0.5">
              View All <span className="material-symbols-outlined text-sm">chevron_right</span>
            </button>
          </div>
          <div className="space-y-2">
            {approvedDoctors.map(doc => (
              <div key={doc.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 flex items-center gap-3">
                <div className="w-10 h-10 bg-[#0B3C95] rounded-full flex items-center justify-center text-white font-black text-sm shrink-0">
                  {(doc.doctorName || 'D').charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-[#0D1B2A] text-sm truncate">{doc.doctorName || `Doctor #${doc.doctorId}`}</p>
                  <p className="text-xs text-gray-400">{doc.doctorSpecialization || 'Doctor'}</p>
                </div>
                <span className="text-xs font-bold px-2 py-0.5 bg-green-50 text-green-600 rounded-full shrink-0">✓ Active</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Access Requests */}
      {accessRequests.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-lg font-extrabold text-[#0D1B2A]">Access Requests</h2>
            <span className="w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">{accessRequests.length}</span>
          </div>
          {message && <p className="text-sm text-green-600 mb-2">{message}</p>}
          {accessRequests.map(req => (
            <div key={req.id} className="bg-white rounded-2xl p-4 border-2 border-[#0B3C95]/20 shadow-sm mb-2">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#0B3C95] rounded-full flex items-center justify-center text-white font-black text-sm">
                  {(req.doctorName || 'D').charAt(0)}
                </div>
                <div className="flex-1">
                  <p className="font-bold text-[#0D1B2A] text-sm">{req.doctorName || `Doctor #${req.doctorId}`}</p>
                  <p className="text-xs text-gray-400">{req.doctorSpecialization} • Wants to view your records</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => handleApprove(req.id)} className="py-2.5 bg-[#0B3C95] text-white font-bold rounded-xl text-xs">✓ Approve</button>
                <button onClick={() => handleDeny(req.id)} className="py-2.5 border border-gray-200 text-gray-600 font-bold rounded-xl text-xs">✗ Deny</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default HomeTab;